import React, { useEffect, useState } from 'react';
import api from '../../services/api';

type NewsSidebarGallery = {
  title: string;
  images: string[];
};

const defaultConfig: NewsSidebarGallery = {
  title: 'Highlights',
  images: [],
};

const AdminNewsGallery: React.FC = () => {
  const [config, setConfig] = useState<NewsSidebarGallery>(defaultConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get<NewsSidebarGallery>('/news-sidebar-gallery');
      setConfig({
        title: res.data?.title || defaultConfig.title,
        images: Array.isArray(res.data?.images) ? res.data.images : [],
      });
    } catch (err) {
      console.error(err);
      setError('Load failed.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await api.put('/news-sidebar-gallery', config);
      await load();
    } catch (err) {
      console.error(err);
      setError('Save failed. Please try again later.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      const res = await api.post<{ url?: string; path?: string }>(
        '/uploads/news-gallery',
        formData,
        { timeout: 60000 }
      );
      const rawPath = res.data.path || '';
      const base = (api.defaults.baseURL as string | undefined) || '';
      const origin = base.startsWith('http') ? new URL(base).origin : window.location.origin;
      const url = rawPath ? new URL(rawPath, origin).toString() : res.data.url || '';
      if (!url) return;

      setConfig((prev) => ({ ...prev, images: [...prev.images, url] }));
    } catch (err) {
      console.error(err);
      alert('Upload failed. Please try again later.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const moveImage = (index: number, dir: -1 | 1) => {
    setConfig((prev) => {
      const list = [...prev.images];
      const target = index + dir;
      if (target < 0 || target >= list.length) return prev;
      [list[index], list[target]] = [list[target], list[index]];
      return { ...prev, images: list };
    });
  };

  const removeImage = (index: number) => {
    setConfig((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  if (loading) return <p className="text-sm text-neutral-600">Loading...</p>;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-serif text-2xl font-semibold text-neutral-900">
          News Sidebar Gallery
        </h1>
        <button
          onClick={handleSave}
          disabled={saving}
          className="rounded-full bg-brand-red px-4 py-1.5 text-xs font-semibold tracking-[0.18em] text-white hover:bg-red-700 disabled:opacity-70"
        >
          {saving ? 'Saving...' : 'Save settings'}
        </button>
      </div>

      <div className="space-y-4 rounded-lg border border-neutral-200 bg-white p-4">
        <div className="text-xs">
          <label className="mb-1 block font-medium text-neutral-700">Card title</label>
          <input
            value={config.title}
            onChange={(e) =>
              setConfig((prev) => ({ ...prev, title: e.target.value }))
            }
            className="w-full rounded-md border border-neutral-300 px-2 py-1.5 outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
            placeholder="Highlights"
          />
        </div>

        <div className="flex items-center justify-between text-xs">
          <span className="font-semibold text-neutral-800">
            Gallery images (ordered)
          </span>
          <label className="inline-flex cursor-pointer items-center rounded-full border border-neutral-300 px-3 py-1 text-xs font-semibold text-neutral-700 hover:bg-neutral-50">
            {uploading ? 'Uploading...' : 'Upload image'}
            <input
              type="file"
              accept="image/*"
              onChange={handleUpload}
              className="hidden"
            />
          </label>
        </div>

        {config.images.length === 0 ? (
          <p className="text-xs text-neutral-500">No images yet. Upload first.</p>
        ) : (
          <div className="grid gap-3 md:grid-cols-3">
            {config.images.map((url, index) => (
              <div
                key={url + index}
                className="overflow-hidden rounded-md border border-neutral-200 bg-neutral-50"
              >
                <div className="aspect-[4/3] w-full overflow-hidden bg-neutral-100">
                  <img
                    src={url}
                    alt={`news-gallery-${index + 1}`}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="flex items-center justify-between px-2 py-1.5 text-[11px] text-neutral-600">
                  <span>Image {index + 1}</span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => moveImage(index, -1)}
                      disabled={index === 0}
                      className="rounded px-1 py-0.5 hover:bg-neutral-200 disabled:opacity-30"
                    >
                      Move up
                    </button>
                    <button
                      type="button"
                      onClick={() => moveImage(index, 1)}
                      disabled={index === config.images.length - 1}
                      className="rounded px-1 py-0.5 hover:bg-neutral-200 disabled:opacity-30"
                    >
                      Move down
                    </button>
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="rounded px-1 py-0.5 text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && <p className="mt-4 text-xs text-red-600">{error}</p>}
    </div>
  );
};

export default AdminNewsGallery;
